/**
 * „Bilder werden geladen …" — der Hinweis, solange ein Mail-Body noch auf
 * Bilder wartet.
 *
 * Warum es ihn braucht: Nach „Externe Inhalte anzeigen" wechselt der Body
 * sichtbar in einen Zwischenzustand — der Text steht, aber wo die Bilder
 * hingehören, klaffen Lücken, und der iframe wächst noch. Ohne ein Wort dazu
 * liest sich das wie eine kaputte Mail. Bilder aus Signaturen und Newslettern
 * kommen von fremden Servern; ein bis zwei Sekunden sind normal.
 *
 * Gezählt wird je Quelle, weil ein aufgefächerter Verlauf MEHRERE Bodies
 * gleichzeitig zeigt (eine Karte je Nachricht) — der Hinweis steht aber nur
 * einmal oben. Die Summe ist die einzige Zahl, die dort stimmt.
 */

/** Stand je Body-Instanz: Schlüssel ist die Nachricht, Wert die Bildanzahl. */
export type BilderStand = Readonly<Record<string, number>>;

/**
 * Schreibt den Stand einer Quelle fort.
 *
 * `0` entfernt den Eintrag, statt ihn auf null zu setzen: Ein geschlossenes
 * Fenster oder eine zugeklappte Karte soll keine Spur hinterlassen, die die
 * Summe später verfälscht.
 */
export function setzeStand(stand: BilderStand, quelle: string, anzahl: number): BilderStand {
    const sicher = Number.isFinite(anzahl) && anzahl > 0 ? Math.floor(anzahl) : 0;
    if (sicher === 0) {
        if (!(quelle in stand)) return stand;
        const next = { ...stand };
        delete next[quelle];
        return next;
    }
    if (stand[quelle] === sicher) return stand;
    return { ...stand, [quelle]: sicher };
}

/** Wie viele Bilder warten insgesamt noch? */
export function summeLadender(stand: BilderStand): number {
    return Object.values(stand).reduce((s, n) => s + (Number.isFinite(n) && n > 0 ? n : 0), 0);
}

/**
 * Der anzuzeigende Satz — `null`, wenn nichts mehr lädt.
 *
 * Die Einzahl bekommt ihre eigene Formulierung: „1 Bilder werden geladen"
 * liest sich wie ein Programmfehler, und genau solche Kleinigkeiten
 * entscheiden, ob eine Meldung vertrauenswürdig wirkt.
 */
export function bilderLadenText(anzahl: number): string | null {
    if (!Number.isFinite(anzahl) || anzahl <= 0) return null;
    return anzahl === 1 ? "Ein Bild wird geladen …" : `${Math.floor(anzahl)} Bilder werden geladen …`;
}
